import './EasySoftwareTestimonials.scss'

import React, { FunctionComponent } from 'react'
import { getLocalizedUrl } from 'Root/Shared/utils/localeURL'
import PuxLink from 'Root/Shared/utils/PuxLink'
import PuxPicture, {
  PuxPictureType,
} from 'Shared/components/content/mediaImage/PuxPicture'
import PuxWysiwyg, {
  PuxWysiwygHtmlBodyType,
} from 'Shared/components/PuxWysiwyg/PuxWysiwyg'
import { t } from 'ttag'
import PuxStructuredData from '../PuxStructuredData/PuxStructuredData'

export interface EasySoftwareTestimonialType {
  contentType: string
  contentItemId: string
  publishedUtc: string
  testimonialName: PuxWysiwygHtmlBodyType
  testimonialOccupation: PuxWysiwygHtmlBodyType
  testimonialCompany: PuxWysiwygHtmlBodyType
  testimonialText: PuxWysiwygHtmlBodyType
  testimonialPhoto: {
    mediaSelectorImage: PuxPictureType
  }
  testimonialLogo?: {
    mediaSelectorImage: PuxPictureType
  }
  testimonialCaseStudy: { internal: boolean; text: string[]; url: string[] }
  testimonialCaseStudyCheckbox: boolean
  pageContentItemId: string
  editorPath: string
}

// const rootClass = `pux-testimonials`

const EasySoftwareTestimonialItem: FunctionComponent<EasySoftwareTestimonialType> = (
  props
) => {
  const caseStudy = props.testimonialCaseStudy
  const cleanBody = props.testimonialText.html.replace(/<[^>]*>/g, ``)
  const testimonialLogo = props.testimonialLogo?.mediaSelectorImage
  const hasTestimonialLogo =
    testimonialLogo?.resizePathsFallback?.some(Boolean) ||
    testimonialLogo?.resizePaths?.some(Boolean)
  const activeEnv = (
    process.env.GATSBY_ACTIVE_ENV ||
    process.env.NODE_ENV ||
    `development`
  ).trim()
  const showTestimonialLogo = [`development`, `stage`].includes(activeEnv)

  function getTestimonialJson(props: EasySoftwareTestimonialType) {
    return {
      "@context": "https://schema.org",
      "@type": "Review",
      "reviewBody": cleanBody,
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": 5,
        "bestRating": 5,
        "worstRating": 1
      },
      "author": {
        "@type": "Person",
        "name": props.testimonialName.html,
        "jobTitle": props.testimonialOccupation.html,
        "affiliation": {
          "@type": "Organization",
          "name": props.testimonialCompany.html
        }
      },
      "datePublished": props.publishedUtc,
      "itemReviewed": {
        "@type": "SoftwareApplication",
        "name": "Easy8",
        "applicationCategory": "ProjectManagementApplication",
        "url": "https://www.easy8.com/",
        "operatingSystem": "Web, iOS, Android",
        "mainEntityOfPage": "https://www.easy8.com/",
      }
    }
  }

  return (
    <div className='Testimonials-item'>
      <PuxStructuredData structuredData={getTestimonialJson(props)} />
      <div className='Testimonials-meta'>
        <div className='Testimonials-author'>
          <div className='Testimonials-img'>
            <PuxPicture
              {...props?.testimonialPhoto?.mediaSelectorImage}
              width={50}
              height={50}
              customAlt={props.testimonialName.html}
            />
          </div>
          <div className='Testimonials-profile'>
            <h4 className='Testimonials-name'>{props.testimonialName.html}</h4>
            <div className='Testimonials-info'>
              <div className='Testimonials-occupation'>
                {props.testimonialOccupation.html}
              </div>
              <div className='Testimonials-company'>
                {props.testimonialCompany.html}
              </div>
            </div>
          </div>
        </div>
        {showTestimonialLogo && testimonialLogo && hasTestimonialLogo ? (
          <div className='Testimonials-logo'>
            <PuxPicture
              {...testimonialLogo}
              width={172}
              height={32}
              customAlt={props.testimonialCompany.html}
            />
          </div>
        ) : null}
      </div>

      <div className='Testimonials-content'>
        <div className='Testimonials-icon'>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='49'
            height='42'
            viewBox='0 0 49 42'
            fill='none'
            aria-hidden='true'
            focusable='false'
          >
            <path
              d='M44.5278 22.5413L48.8055 23.1193V42H28.3889V16.5688C28.3889 6.55047 33.0556 0 43.75 0H49V10.4037H44.7222C41.8056 10.4037 39.8611 12.3303 39.8611 15.2202V17.1468C39.8611 21 41.6111 21.7706 44.5278 22.5413ZM16.1389 22.5413L20.2222 23.1193V42H0V16.5688C0 6.55047 4.66667 0 15.3611 0H20.6111V10.4037H16.3333C13.4167 10.4037 11.4722 12.3303 11.4722 15.2202V17.1468C11.4722 21 13.0278 21.7706 16.1389 22.5413Z'
              fill='#BCC5FF'
            />
          </svg>
        </div>
        <PuxWysiwyg
          className='Testimonials-text'
          content={props.testimonialText}
          inline
          wysiwygId={props.contentItemId}
        />
      </div>

      {(caseStudy?.url?.length > 0 && !caseStudy?.url?.includes("null") && !caseStudy?.url?.includes(null)) ? (
        <div className='Testimonials-caseStudy'>
          <PuxLink
            to={getLocalizedUrl(caseStudy.url[0])}
            target={props.testimonialCaseStudyCheckbox ? `_blank` : `_self`}
          >
            {caseStudy.text[0]
              ? `${caseStudy.text[0]} ->`
              : `${[t`EasyRedmine.Testimonial.CaseStudyLinkText`]} ->`}
          </PuxLink>
        </div>
      ) : null}
    </div>
  )
}

export default EasySoftwareTestimonialItem
